import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function DateRangeFilter({
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  label = "Date range",
  fromLabel = "From",
  toLabel = "To",
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const margin = 8;
      const width = Math.min(360, Math.max(0, window.innerWidth - margin * 2));
      const height = panelRef.current?.getBoundingClientRect().height || 230;
      const maxHeight = Math.max(0, window.innerHeight - margin * 2);
      const below = rect.bottom + 6;
      const top = below + height <= window.innerHeight - margin
        ? below
        : Math.max(margin, rect.top - Math.min(height, maxHeight) - 6);
      setPosition({
        top: Math.min(top, Math.max(margin, window.innerHeight - Math.min(height, maxHeight) - margin)),
        left: Math.max(margin, Math.min(rect.right - width, window.innerWidth - width - margin)),
        width,
        maxHeight,
      });
    };
    const onScroll = (event) => {
      if (!(event.target instanceof Node) || !panelRef.current?.contains(event.target)) updatePosition();
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, position?.width]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!wrapperRef.current?.contains(event.target) && !panelRef.current?.contains(event.target)) setOpen(false);
    };

    const closeOnEscape = (event) => {
      if (event.key === "Escape" && (wrapperRef.current?.contains(document.activeElement) || panelRef.current?.contains(document.activeElement))) {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const summary =
    fromValue && toValue
      ? `${fromValue} to ${toValue}`
      : fromValue
        ? `From ${fromValue}`
        : toValue
          ? `Through ${toValue}`
          : label;

  return (
    <div className="nw-date-range-filter" ref={wrapperRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`nw-date-range-filter__toggle ${open ? "open" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="nw-date-range-filter__summary">
          <i className="fa-regular fa-calendar" aria-hidden="true" />
          <span>{summary}</span>
        </span>
        <i className="fa-solid fa-chevron-down" aria-hidden="true" />
      </button>

      {open && createPortal(
        <div className="nw-date-range-filter__panel" ref={panelRef} role="dialog" aria-label={label}
          style={{ position: "fixed", ...position, right: "auto", visibility: position ? "visible" : "hidden" }}>
          <div className="nw-date-range-filter__fields">
            <label>
              <span>{fromLabel}</span>
              <input
                type="date"
                className="form-control"
                value={fromValue}
                max={toValue || undefined}
                onChange={(event) => onFromChange(event.target.value)}
              />
            </label>
            <label>
              <span>{toLabel}</span>
              <input
                type="date"
                className="form-control"
                value={toValue}
                min={fromValue || undefined}
                onChange={(event) => onToChange(event.target.value)}
              />
            </label>
          </div>
          <div className="nw-date-range-filter__actions">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={!fromValue && !toValue}
              onClick={() => {
                onFromChange("");
                onToChange("");
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
        </div>,
        wrapperRef.current?.closest('.modal, .offcanvas, [aria-modal="true"]') || document.body,
      )}
    </div>
  );
}
